import { useCallback, useEffect, useState } from 'react';
import { trackAnalyticsEvent, PRODUCT_AREA } from '@leagueplatform/analytics';
import {
  getAllAppointmentIframeAllowedOrigins,
  isMockAppointmentIframeEnabled,
} from 'common/env';
import {
  ANALYTICS_EVENT_NAMES,
  ANALYTICS_SCREEN_NAMES,
  BookingSource,
  SUB_PRODUCT_AREA_NAMES,
} from 'common/constants';
import { getBookingSourceFromOrigin } from 'utils/origin-system-id';

export const BOOKING_MESSAGE_TYPES = {
  /** Fired when the user successfully completes a booking. */
  BOOKING_COMPLETED: 'BOOKING_COMPLETED',
  /** Fired when the user completes a booking and should be returned home. */
  BOOKING_COMPLETED_GO_HOME: 'BOOKING_COMPLETED_GO_HOME',
} as const;

type BookingMessageType =
  (typeof BOOKING_MESSAGE_TYPES)[keyof typeof BOOKING_MESSAGE_TYPES];

export type BookingFlowStatus = 'pending' | 'completed' | 'completed-go-home';

/**
 * Reports a completed booking, attributed to the system that owns the iframe
 * origin the completion message came from. This is deliberately the message
 * origin rather than the system the app is running under.
 */
function trackBookingCompleted(bookingSource: BookingSource) {
  trackAnalyticsEvent(ANALYTICS_EVENT_NAMES.BOOKING_COMPLETED, {
    product_area: PRODUCT_AREA.GET_CARE,
    sub_product_area: SUB_PRODUCT_AREA_NAMES.APPOINTMENT,
    screen_name: ANALYTICS_SCREEN_NAMES.APPOINTMENT_BOOKING_IFRAME,
    booking_source: bookingSource,
  });
}

function isBookingMessage(data: unknown): data is { type: BookingMessageType } {
  if (typeof data !== 'object' || data === null) return false;
  const { type } = data as { type?: unknown };
  return (
    type === BOOKING_MESSAGE_TYPES.BOOKING_COMPLETED ||
    type === BOOKING_MESSAGE_TYPES.BOOKING_COMPLETED_GO_HOME
  );
}

export function useIframeFlowStatus(): { status: BookingFlowStatus } {
  const [status, setStatus] = useState<BookingFlowStatus>('pending');

  useEffect(() => {
    if (!isMockAppointmentIframeEnabled()) {
      const origins = getAllAppointmentIframeAllowedOrigins();
      if (origins.length === 0) {
        // eslint-disable-next-line no-console
        console.warn(
          '[patient-portal] useIframeFlowStatus: neither ' +
            'VITE_AMPLAR_APPOINTMENT_IFRAME_ALLOWED_ORIGINS nor ' +
            'VITE_MY_HEALTH_APPOINTMENT_IFRAME_ALLOWED_ORIGINS is configured. ' +
            'All inbound postMessage events will be blocked. Set these to the ' +
            'iframe origin(s) to enable the booking flow.',
        );
      }
    }
  }, []);

  const handleMessage = useCallback((event: MessageEvent) => {
    const bookingSource = getBookingSourceFromOrigin(event.origin);

    const isTrustedOrigin =
      bookingSource !== null ||
      (isMockAppointmentIframeEnabled() &&
        event.origin === window.location.origin);

    if (!isTrustedOrigin) {
      return;
    }

    if (!isBookingMessage(event.data)) {
      return;
    }

    if (event.data.type === BOOKING_MESSAGE_TYPES.BOOKING_COMPLETED) {
      if (bookingSource) {
        trackBookingCompleted(bookingSource);
      }
      setStatus('completed');
      return;
    }

    if (event.data.type === BOOKING_MESSAGE_TYPES.BOOKING_COMPLETED_GO_HOME) {
      setStatus('completed-go-home');
    }
  }, []);

  useEffect(() => {
    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [handleMessage]);

  return { status };
}
